import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {applyTableWidths, setTheadTBodyHeight} from "../Model/TableModel";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 */

const CustomExpandableTableApp = ({
                                      tableId,
                                      checkbox,
                                      thead,
                                      tbody,
                                      inModal,
                                      onRowExpand,
                                      withoutHeight
                                  }) => {
    const [openRowId, setOpenRowId] = useState(null);
    const elementRef = useRef(null);
    const tbodyRef = useRef(null);
    const rafRef = useRef(null);
    const { t } = useTranslation();
    const rows = Array.isArray(tbody) ? tbody : (tbody?.body || []);

    /**
     * Signature of what the widths actually depend on: the columns and the rows
     * painted. Parents rebuild the `columns` and `rows` arrays on every render,
     * so depending on their identity recalculated the widths on any unrelated
     * state change, like opening the options menu of a row.
     */
    const layoutKey = useMemo(
        () => JSON.stringify({
            thead: (thead || []).map((col) => [col?.field, col?.label, col?.customWidth, col?.fixed]),
            rows: rows.map((row) => row?.id),
            checkbox,
        }),
        [thead, rows, checkbox],
    );

    // Keeps the calculation reading the current data without changing its identity
    const latestDataRef = useRef({ rows, thead, checkbox });
    latestDataRef.current = { rows, thead, checkbox };

    /**
     * Executes the column width calculation logic for the table.
     *
     * Delegates the width computation and distribution to the `applyTableWidths`
     * utility function, ensuring widths are applied based on current data and layout.
     *
     * @returns {void}
     */
    const applyColumnWidths = useCallback(() => {
        const { rows: currentRows, thead: currentThead, checkbox: currentCheckbox } = latestDataRef.current;
        applyTableWidths({ tableId, rows: currentRows, checkbox: currentCheckbox, thead: currentThead });
    }, [tableId]);

    // Keeps the resize observer pointing to the latest data without resubscribing on every render
    const applyColumnWidthsRef = useRef(applyColumnWidths);
    applyColumnWidthsRef.current = applyColumnWidths;

    /**
     * Initializes layout-related CSS variables and performs a one-time
     * column width calculation after the first render cycle.
     *
     * - Applies dynamic positioning using `setTheadTBodyHeight`
     * - Ensures column widths are calculated only once using a ref flag
     * - Uses requestAnimationFrame to defer execution until layout is stable
     *
     * Dependencies:
     * - layoutKey: changes only when the columns or the rows painted change
     * - inModal: affects layout positioning
     * - withoutHeight: controls height behavior
     *
     * Layout effect so the height and the widths are applied before the paint that follows the response,
     * when the loader is already hidden (the frame requested here still runs before that paint).
     */
    useLayoutEffect(() => {
        setTheadTBodyHeight({withoutHeight, elementRef, tbodyRef, inModal});

        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        rafRef.current = requestAnimationFrame(() => {
            applyColumnWidths();
        });

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [applyColumnWidths, layoutKey, inModal, withoutHeight]);

    /**
     * Observes container size changes and recalculates column widths when needed.
     *
     * Keeps the columns filling the available space when the layout changes after a
     * search, a pagination change or a window resize.
     *
     * @returns {void}
     */
    useEffect(() => {
        if (!elementRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);

            rafRef.current = requestAnimationFrame(() => {
                applyColumnWidthsRef.current();
            });
        });

        resizeObserver.observe(elementRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    /**
     * Toggles the expansion state of a table row.
     *
     * If the selected row is not currently expanded, it triggers the optional
     * `onRowExpand` callback before updating the state.
     *
     * @param {string|number} rowId - Unique identifier of the row to toggle.
     * @param {Object} row - Full row data object associated with the rowId.
     * @returns {void}
     */
    const toggleRow = (rowId, row) => {
        const isExpanding = openRowId !== rowId;

        if (isExpanding && onRowExpand) {
            onRowExpand(row);
        }
        setOpenRowId(isExpanding ? rowId : null);
    };

    return {
        t,
        elementRef,
        tbodyRef,
        openRowId,
        toggleRow
    };
};

export default CustomExpandableTableApp;
